import {useForm, GenericObject, InvalidSubmissionContext, FormContext, FormOptions} from "vee-validate";
import {ref, Ref, unref} from "vue";

export const NON_FIELD_ERROR_KEYS = ["_All__", "__all__"];

/**
 * Creates a function for handling a response from mutation based on a Django form. This function is intended
 * to be used in a promise resolution chain:
 *
 * ```js
 * mutate(params).then(
 *     handleDjangoForm(this.$refs.form)
 * ).then((result) => {
 *     // Handle result.
 * });
 * ```
 *
 * The returned function checks whether the returned data contains any errors. If there are no errors, it will resolve
 * the promise with the form response - the returned object contains directly the mutation object, there is no
 * need to extract it from the data.
 *
 * If the response contains any errors, they will be set on the given form reference and the promise will be rejected.
 *
 * @param formRef Reference to a vee-validate form component.
 * @param nonFieldErrorsRef When set to a ref, this ref will be populated with a list of non-field errors.
 * @param mutationName Name of the mutation - will be used to extract the data. When not specified, the first key
 *      in the object will be used - this should work in most cases.
 * @returns {function({data?: *}): (Promise<Object>)} Form response or promise rejection in case of an error.
 */
export function handleDjangoForm(formRef, nonFieldErrorsRef = null, mutationName = null) {
    return function ({data}) {
        let mutationResponse;
        if (mutationName) {
            mutationResponse = data[mutationName];
        } else {
            mutationResponse = Object.values(data)[0];
        }

        if (mutationResponse.errors === undefined) {
            console.error(
                'The response payload does not contain list of errors. This prevents handleDjangoForm from ' +
                'validating that the form was successfully submitted.'
            );
            return Promise.reject(mutationResponse);
        }

        if (mutationResponse.errors.length > 0) {
            for (const error of mutationResponse.errors) {
                if (NON_FIELD_ERROR_KEYS.includes(error.field) && nonFieldErrorsRef !== null) {
                    nonFieldErrorsRef.value = error.messages;
                    continue;
                }

                if (formRef instanceof Function) {
                    formRef(error.field, error.messages[0]);
                } else {
                    formRef.setFieldError(error.field, error.messages[0]);
                }
            }

            return Promise.reject(mutationResponse);
        }

        return Promise.resolve(mutationResponse);
    };
}

const vuetifyFieldOptions = {
    props(state) {
        return {
            'error-messages': state.errors,
        };
    },
}

/**
 *
 * @param {FormOptions<GenericObject, GenericObject, FormSchema<GenericObject> | TypedSchema<GenericObject, GenericObject>>} options
 * @returns {FormContext<GenericObject, GenericObject> & {nonFieldErrors: Ref<string[]>, handleMutationSubmit: (
 *     mutation: (values: GenericObject) => Promise<{data}>,
 *     handlers: {
 *         buildValues: (values: GenericObject, ctx) => GenericObject,
 *         onSuccess: (values: Object) => Any,
 *         onInvalidFormResponse: (response: Object) => Any,
 *         onMutationError: (error: Error) => Any,
 *         onSubmitValidationError: (ctx: InvalidSubmissionContext<GenericObject, GenericObject>) => Any,
 *         onFinally: () => Any,
 *     },
 * ) => (e?: Event) => Promise<Any>}}
 */
export function useDjangoForm(options) {
    const form = useForm(options);
    form.nonFieldErrors = ref([]);

    const defineField = form.defineField;
    form.defineField = (name, options) => {
        let fieldOptions;
        if (options === undefined) {
            fieldOptions = vuetifyFieldOptions;
        } else if (options instanceof Function) {
            fieldOptions = (state) => {
                const result = options(state);
                const newProps = vuetifyFieldOptions.props(state);
                result.props = {...newProps, ...result.props};
                return result;
            };
        } else if (options.props) {
            fieldOptions = options;
            const originalProps = fieldOptions.props;
            fieldOptions.props = (state) => {
                return {...vuetifyFieldOptions.props(state), ...originalProps(state)};
            }
        } else {
            fieldOptions = {...vuetifyFieldOptions, ...options};
        }

        return defineField(name, fieldOptions);
    }

    /**
     * @param {(values: GenericObject) => Promise<{data}>} mutation
     * @param {(values: GenericObject, ctx) => GenericObject} buildValues
     * @param {(values: Object) => Any} onSuccess
     * @param {(response: Object) => Any} onInvalidFormResponse
     * @param {(error: Error) => Any} onMutationError
     * @param {(ctx: InvalidSubmissionContext<GenericObject, GenericObject>) => Any} onSubmitValidationError
     * @param {() => Any} onFinally
     * @returns {(e?: Event) => Promise<Any>}
     */
    form.handleMutationSubmit = (mutation, {buildValues, onSuccess, onInvalidFormResponse, onMutationError, onSubmitValidationError, onFinally}) => {
        return form.handleSubmit((values, ctx) => {
            if (form.nonFieldErrors.value.length > 0) {
                form.nonFieldErrors.value = [];
            }

            if (buildValues !== undefined) {
                const buildValuesResult = buildValues(values, ctx);
                if (buildValuesResult !== undefined && buildValuesResult !== null) {
                    values = buildValuesResult;
                }
            }

            return mutation(values)
                .then(handleDjangoForm(form.setFieldError, form.nonFieldErrors))
                .then((response) => {
                    if (onSuccess !== undefined) {
                        return onSuccess(response);
                    }
                    return response;
                })
                .catch((error) => {
                    if (error instanceof Error) {
                        if (onMutationError !== undefined) {
                            return onMutationError(error);
                        } else {
                            console.error('Error while submitting the form: %o', error);
                        }
                    } else {
                        if (onInvalidFormResponse !== undefined) {
                            return onInvalidFormResponse(error);
                        }
                    }
                    return error;
                }).finally(() => {
                    if (onFinally !== undefined) {
                        return onFinally();
                    }
                });
        }, (ctx) => {
            if (form.nonFieldErrors.value.length > 0) {
                form.nonFieldErrors.value = [];
            }

            if (onSubmitValidationError !== undefined) {
                return onSubmitValidationError(ctx);
            }
        });
    }

    return form;
}

export function preprocessDjangoErrors(errors) {
    const result = {};
    for (const [field, messages] of Object.entries(unref(errors))) {
        result[field] = messages[0];
    }
    return result;
}
